import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Spin,
  Modal,
  message,
  Drawer,
  Descriptions,
  DatePicker,
  Menu,
  Dropdown,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  UserAddOutlined,
  InboxOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import { useNavigate } from "react-router-dom";
import useNavHeading from "../../../Hooks/CommonHooks/useNavHeading ";
import { useSelector, useDispatch } from "react-redux";
import ProtectedSection from "../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../config/permission";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import {
  fetchVisitorsThunk,
  updateVisitorThunk,
} from "../../../Store/Slices/Admin/visitorManagement/visitor.action";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import PdfFormat from "./PdfFormat";
import Layout from "../../../Components/Common/Layout";
import TableShimmer from "../../../Components/Common/TableShimmer";
import { FaUsers, FaUserCheck, FaUserPlus, FaUserAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { CiSearch } from "react-icons/ci";
import { FiFilter, FiUserPlus } from "react-icons/fi";

const { confirm } = Modal;
const { RangePicker } = DatePicker;

const getCategoryColor = (category) => {
  switch (category) {
    case "candidate": return "blue";
    case "vendor": return "orange";
    case "guest": return "cyan";
    case "client": return "purple";
    case "vip": return "magenta";
    default: return "default";
  }
};

const downloadPDF = async (selectedData, title) => {
  const doc = window.document.getElementById("visitorPdf");
  const element = doc;
  const canvas = await html2canvas(element, { scale: 2 });
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const ratioX = pdfWidth / canvasWidth;
  const ratioY = pdfHeight / canvasHeight;
  const scaleFactor = Math.min(ratioX, ratioY);
  const imgWidth = canvasWidth * scaleFactor;
  const imgHeight = canvasHeight * scaleFactor;
  const marginX = (pdfWidth - imgWidth) / 2;
  pdf.addImage(imgData, "PNG", marginX, 0, imgWidth, imgHeight);
  const fileName = `${selectedData?.schoolId?.nameOfSchool}_${title}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdf.save(fileName);
};

const getStatusColor = (status) => {
  switch (status) {
    case "checked-in": return "green";
    case "pending": return "gold";
    case "sent": return "blue";
    case "checked-out": return "red";
    case "cancelled": return "grey";
    default: return "default";
  }
};

/* status pill classes */
const statusPillClass = (status) => {
  switch (status) {
    case "checked-in":
      return "bg-green-50 text-green-600 border-green-200";
    case "pending":
    case "sent":
      return "bg-yellow-50 text-yellow-600 border-yellow-200";
    case "checked-out":
      return "bg-gray-100 text-gray-500 border-gray-200";
    case "cancelled":
      return "bg-red-50 text-red-500 border-red-200";
    default:
      return "bg-gray-50 text-gray-500 border-gray-100";
  }
};

const VisitorMain = () => {
  const { t } = useTranslation("common");
  useNavHeading(t("Visitor Management"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { visitors, loading, currentPage, totalPages, totalVisitors, count } =
    useSelector((state) => state.admin.visitor || {});

  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    const [startDate = "null", endDate = "null"] = dateRange || [];
    dispatch(
      fetchVisitorsThunk({
        page: currentPage,
        limit: pageSize,
        search: searchText,
        startDate,
        endDate,
      })
    );
  }, [searchText, dateRange]);

  const handlePageChange = (page, newPageSize) => {
    setPageSize(newPageSize);
    dispatch(
      fetchVisitorsThunk({
        page,
        limit: pageSize,
        search: searchText,
        startDate: null,
        endDate: null,
      })
    );
  };

  const handleSearch = (e) => setSearchText(e.target.value);

  const handleAction = (record) => {
    if (record.status === "checked-in") {
      confirm({
        title: t("Do you want to check out this visitor?"),
        icon: <ExclamationCircleOutlined />,
        content: `${t("Checking out")} ${record.visitorName}`,
        onOk() {
          const checkOutData = { status: "checked-out", checkOutTime: new Date().toISOString() };
          dispatch(updateVisitorThunk({ id: record._id, data: checkOutData }))
            .unwrap()
            .then(() => {
              message.destroy();
              message.success(`${t("Checked out")} ${record.visitorName} ${t("successfully!")}`);
              dispatch(fetchVisitorsThunk({ page: currentPage, limit: pageSize, search: searchText, startDate: "null", endDate: "null" }));
            })
            .catch(() => { message.destroy(); message.error(`${t("Failed to check out")} ${record.visitorName}.`); });
        },
      });
    } else if (record.status === "pending" || record.status === "sent") {
      confirm({
        title: t("Do you want to check in this visitor?"),
        icon: <ExclamationCircleOutlined />,
        content: `${t("Checking in")} ${record.visitorName}`,
        onOk() {
          const checkInData = { status: "checked-in", checkInTime: new Date().toISOString() };
          dispatch(updateVisitorThunk({ id: record._id, data: checkInData }))
            .unwrap()
            .then(() => {
              message.destroy();
              message.success(`${t("Checked in")} ${record.visitorName} ${t("successfully!")}`);
              dispatch(fetchVisitorsThunk({ page: currentPage, limit: pageSize, search: searchText, startDate: "null", endDate: "null" }));
            })
            .catch(() => { message.destroy(); message.error(`${t("Failed to check in")} ${record.visitorName}.`); });
        },
      });
    }
  };

  const handleViewBadge = (record) => {
    setSelectedVisitor(record);
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Visitor")}
        </span>
      ),
      dataIndex: "visitorName",
      key: "visitorName",
      render: (text, record) => {
        const trimmedName = text ? text.split(" ").slice(0, 2).join(" ") : "N/A";
        return (
          <div className="flex items-center gap-3">
            {record.photo ? (
              <img
                src={record.photo}
                alt={trimmedName}
                className="w-9 h-9 rounded-full object-cover border border-gray-100 flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                <UserOutlined className="text-purple-400 text-sm" />
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-gray-800 leading-tight capitalize">
                {trimmedName}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                VST-{record._id?.slice(-3).toUpperCase()}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Purpose")}
        </span>
      ),
      dataIndex: "purposeOfVisit",
      key: "purposeOfVisit",
      render: (text) => (
        <span className="text-sm text-gray-600 capitalize">{text || t("N/A")}</span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Host")}
        </span>
      ),
      dataIndex: "hostEmployee",
      key: "hostEmployee",
      render: (host) => (
        <span className="text-sm text-gray-600">
          {host ? `${host.firstName || ""} ${host.lastName || ""}`.trim() : t("N/A")}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Check In")}
        </span>
      ),
      dataIndex: "checkInTime",
      key: "checkInTime",
      render: (time) => (
        <span className="text-sm text-gray-700">
          {time ? new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Check Out")}
        </span>
      ),
      dataIndex: "checkOutTime",
      key: "checkOutTime",
      render: (time) => (
        <span className="text-sm text-gray-700">
          {time ? new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Status")}
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          className={`inline-flex px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${statusPillClass(status)}`}
        >
          {status?.replace("-", " ")}
        </span>
      ),
    },
    {
      title: (
         <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Actions")}
        </span>
      ),
      key: "actions",
      width: 48,
      render: (_, record) => {
        const menu = (
          <Menu>
            {record.status === "checked-in" && (
              <Menu.Item key="checkout" onClick={() => handleAction(record)}>
                {t("Check-out")}
              </Menu.Item>
            )}
            {(record.status === "pending" || record.status === "sent") && (
              <Menu.Item key="checkin" onClick={() => handleAction(record)}>
                {t("Check-in")}
              </Menu.Item>
            )}
            <Menu.Item key="badge" onClick={() => handleViewBadge(record)}>
              {t("View Badge")}
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
              <MoreOutlined className="text-base" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <AdminDashLayout>
      <ProtectedSection
        requiredPermission={PERMISSIONS.VIEW_VISITOR_MANAGEMENT}
        title={"Visitor Management"}
      >
        <div className="min-h-screen bg-gray-50 p-4 flex flex-col gap-5">

          {/* ── Page Header ───────────────────────────────── */}
          <div className="flex items-start justify-between gap-4 bg-white rounded-xl p-4 shadow-sm">
            <div>
              <h1 className="page-heading">
                {t("Visitor Management")}
              </h1>
              <p className="subheading">
                {t("Track and manage all campus visitors in real-time.")}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <ProtectedAction requiredPermission={PERMISSIONS.PRE_REGISTRATION}>
                <button
                  onClick={() => navigate("/preregistered-visitor")}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-purple-600 bg-purple-50 border border-purple-200 hover:bg-purple-100 transition whitespace-nowrap"
                >
                  {t("Pre Registration")}
                </button>
              </ProtectedAction>
              <ProtectedAction requiredPermission={PERMISSIONS.ADD_VISITOR}>
                <button
                  onClick={() => navigate("/direct-visitor")}
                  className="flex items-center gap-2 px-4 py-2 btn-gradient"
                >
                  <FiUserPlus className="w-4 h-4" />
                  {t("Log New Visitor")}
                </button>
              </ProtectedAction>
            </div>
          </div>

          {/* ── Stat Cards ────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {t("Total Today")}
              </p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {count?.visitorsToday ?? 0}
              </p>
              <p className="text-xs font-semibold text-green-500 mt-1">
                +12% {t("from yesterday")}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {t("Currently In")}
              </p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {count?.currentlyInside ?? 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t("Active visitors on campus")}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {t("Waiting")}
              </p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {count?.preRegisteredVisitors ?? 0}
              </p>
              <p className="text-xs font-semibold text-yellow-500 mt-1">
                {t("Awaiting host approval")}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {t("Avg. Duration")}
              </p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {count?.directVisitors ?? 0}
                <span className="text-xl font-bold text-gray-500 ml-0.5">m</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t("Average visit time")}
              </p>
            </div>
          </div>

          {/* ── Visitor Log Card ──────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">
                {t("Visitor Log")}
              </h2>
              <div className="flex items-center gap-2">
                {/* Date range */}
                <RangePicker
                  size="middle"
                  onChange={(dates) => setDateRange(dates)}
                  format="YYYY-MM-DD"
                  className="rounded-lg"
                />
                {/* Search */}
                <div className="relative flex items-center">
                  <CiSearch className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder={t("Search visitors...")}
                    value={searchText}
                    onChange={handleSearch}
                    className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 w-48"
                  />
                </div>
                {/* Filter icon */}
                <button className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition">
                  <FiFilter className="w-3.5 h-3.5" />
                </button>
                {/* Reset */}
                <button
                  onClick={() => { setDateRange([]); setSearchText(""); }}
                  className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition"
                  title={t("Reset")}
                >
                  <ReloadOutlined className="text-sm" />
                </button>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <TableShimmer columns={7} />
            ) : (
              <Table
                columns={columns}
                dataSource={visitors}
                rowKey="_id"
                rowClassName="hover:bg-gray-50 transition-colors"
                scroll={{ x: 900 }}
                pagination={{
                  current: currentPage,
                  total: totalVisitors,
                  pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
                  size: "small",
                  showTotal: (total) =>
                    `Page ${currentPage} of ${totalPages} | Total ${total} records`,
                  onChange: handlePageChange,
                  onShowSizeChange: (current, size) => {
                    setPageSize(size);
                    dispatch(fetchVisitorsThunk({ page: 1, limit: size, searchText, startDate: "null", endDate: "null" }));
                  },
                }}
              />
            )}
          </div>
        </div>
      </ProtectedSection>

      {/* ── Visitor Details Drawer ───────────────────────── */}
      <Drawer
        title={
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-gray-800">
              {t("Visitor Details")}
            </span>
            {selectedVisitor?.status && (
              <span
                className={`inline-flex px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${statusPillClass(selectedVisitor.status)}`}
              >
                {selectedVisitor.status?.replace("-", " ")}
              </span>
            )}
          </div>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={selectedVisitor?.visitType === "direct" ? 420 : 900}
        styles={{ body: { background: "#f9fafb", padding: "20px" } }}
      >
        {selectedVisitor?.visitType === "direct" ? (
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                {t("Visitor Information")}
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { label: t("Visitor Name"), value: selectedVisitor?.visitorName },
                  { label: t("Contact"), value: selectedVisitor?.contact },
                  { label: t("Company"), value: selectedVisitor?.company },
                  { label: t("Purpose"), value: selectedVisitor?.purposeOfVisit },
                  { label: t("Visit Type"), value: selectedVisitor?.visitType },
                  {
                    label: t("Host Employee"),
                    value: selectedVisitor?.hostEmployee
                      ? `${selectedVisitor.hostEmployee.firstName || ""} ${selectedVisitor.hostEmployee.lastName || ""}`.trim()
                      : t("N/A"),
                  },
                  {
                    label: t("Check-in Time"),
                    value: selectedVisitor?.checkInTime
                      ? new Date(selectedVisitor.checkInTime).toLocaleString()
                      : "N/A",
                  },
                  {
                    label: t("Check-out Time"),
                    value: selectedVisitor?.checkOutTime
                      ? new Date(selectedVisitor.checkOutTime).toLocaleString()
                      : t("N/A"),
                  },
                  {
                    label: t("Created At"),
                    value: selectedVisitor?.createdAt
                      ? new Date(selectedVisitor.createdAt).toLocaleString()
                      : t("N/A"),
                  },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between items-start py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold text-gray-700 capitalize text-right max-w-[60%]">
                      {item.value || t("N/A")}
                    </span>
                  </div>
                ))}

                <div className="flex justify-between items-start py-1.5">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    {t("Category")}
                  </span>
                  <Tag color={getCategoryColor(selectedVisitor?.category)} className="capitalize m-0">
                    {selectedVisitor?.category || t("N/A")}
                  </Tag>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <PdfFormat initialVisitorData={selectedVisitor} />
            </div>
            <button
             
              onClick={() => downloadPDF(selectedVisitor, "visitorPdf")}
              className="btn-gradient flex justify-center items-center space-x-2 text-lg  border-none font-medium shadow-md hover:shadow-lg transition-all"
            >
               <PlusOutlined />
              <span>{t("Download PDF")}</span>
            </button>
          </div>
        )}
      </Drawer>
    </AdminDashLayout>
  );
};

export default VisitorMain;